// ResetPassword Component - Handle password reset from email link
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';
import './AuthModal.css'; // Reuse auth modal styles

const ResetPassword = ({ onComplete }) => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    // Get token from URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    
    if (urlToken) {
      setToken(urlToken);
    } else {
      setTokenValid(false);
      setError('No reset token provided. Please request a new password reset.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    // Clear URL params and go to home
    window.history.replaceState({}, document.title, '/');
    if (onComplete) {
      onComplete('login');
    }
  };

  const handleGoHome = () => {
    window.history.replaceState({}, document.title, '/');
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className="auth-modal-overlay" style={{ position: 'fixed' }}>
      <div className="auth-modal">
        <div className="auth-modal-header">
          <img src="/iggy-icon.jpg" alt="Iggy" className="auth-modal-logo" />
          <h2>{success ? 'Password Reset!' : 'Reset Password'}</h2>
        </div>

        {success ? (
          <div className="auth-form">
            <div className="auth-success">
              Your password has been reset successfully.
            </div>
            <button 
              onClick={handleGoToLogin}
              className="auth-submit"
            >
              Log In with New Password
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error">{error}</div>}

            {tokenValid && (
              <>
                <div className="auth-field">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                </div>

                <button 
                  type="submit" 
                  className="auth-submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </>
            )}

            {!tokenValid && (
              <button 
                type="button"
                onClick={handleGoHome}
                className="auth-submit"
              >
                Go to Home
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
